/**
 * File validation — single source of truth for extension allow/block lists
 * and size limits, shared across all dropzones in tenant + admin frontends.
 *
 * IMPORTANT: Keep the extension lists synchronised with:
 *   - tenant/backend/app/core/file_validation.py
 *   - platform-admin/backend/app/core/file_validation.py
 *
 * The backend is the authoritative check (API clients can bypass frontend
 * validation). Frontend validation is a UX layer that gives fast feedback.
 */

// ── Extension lists ───────────────────────────────────────────────

/** Dangerous executable extensions — always rejected regardless of accept mode. */
export const BLOCKED_EXTENSIONS: readonly string[] = [
  '.exe', '.bat', '.cmd', '.sh', '.ps1', '.msi', '.dll',
  '.com', '.scr', '.vbs', '.js', '.wsf', '.pif',
];

export const IMAGE_EXTENSIONS: readonly string[] = [
  '.png', '.jpg', '.jpeg', '.gif', '.webp', '.svg', '.bmp', '.tiff',
];

/** The workshop's logo: only what the invoicing provider takes, so a logo
 *  that uploads always reaches the legal PDF (owner decision, 2026-09-27 —
 *  no SVG, BMP or TIFF). Mirrors `LOGO_EXTENSIONS` in
 *  `bolloprint_shared/file_validation.py`, which the server enforces. */
export const LOGO_EXTENSIONS: readonly string[] = ['.png', '.jpg', '.jpeg', '.webp', '.gif'];

export const DESIGN_3D_EXTENSIONS: readonly string[] = [
  '.stl', '.obj', '.3mf', '.step', '.stp', '.gcode', '.bgcode',
  // Keep in sync with shared/py/bolloprint_shared/file_validation.py.
  '.goo',
  '.chitubox', '.ctb', '.cbddlp', '.photon', '.lys', '.pwmx',
];

/** Sliced files a Piece accepts, longest suffix first.
 *
 *  Matched with `endsWith`, never `splitext`: the extension of `foo.gcode.3mf`
 *  is `.3mf`, and a BARE `.3mf` is a PrusaSlicer *project* file that carries no
 *  weight or time estimates at all — accepting it would offer an import that
 *  can never fill anything in. Mirrors `SLICED_SUFFIXES` in
 *  tenant/backend/app/services/sliced/__init__.py. */
export const SLICED_SUFFIXES: readonly string[] = ['.gcode.3mf', '.bgcode', '.gcode', '.goo'];

/** The sliced suffix of `filename`, or null when it isn't one we accept. */
export function matchSlicedSuffix(filename: string): string | null {
  const name = (filename || '').trim().toLowerCase();
  return SLICED_SUFFIXES.find(s => name.endsWith(s)) ?? null;
}

export const DOC_EXTENSIONS: readonly string[] = [
  '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.csv', '.txt',
];

export const ARCHIVE_EXTENSIONS: readonly string[] = [
  '.zip', '.rar', '.7z', '.gz',
];

// ── Size limits (bytes) ───────────────────────────────────────────

export const MAX_SIZE_IMAGE = 25 * 1024 * 1024;           // 25 MB
export const MAX_SIZE_DOC = 25 * 1024 * 1024;             // 25 MB
export const MAX_SIZE_3D = 5 * 1024 * 1024 * 1024;        // 5 GB (presigned PUT direct-to-S3)
export const MAX_SIZE_DEV_ATTACHMENT = 50 * 1024 * 1024;  // 50 MB
export const MAX_SIZE_ANY = 500 * 1024 * 1024;            // 500 MB — orders/expenses/temp mixed bag

// ── Count limits ──────────────────────────────────────────────────

/** Max number of photos per gallery scope (Design + its Pieces, Product
 *  gallery, Etsy import). Mirrors the backend ``PHOTO_CAP`` /
 *  ``PRODUCT_PHOTO_CAP`` constants — keep all three in sync. */
export const MAX_PHOTOS = 20;

// ── Types ─────────────────────────────────────────────────────────

/** `logo` is `image` narrowed to `LOGO_EXTENSIONS`; on the wire it travels as
 *  `image` (the server derives the mode from the category and checks the
 *  logo's format when it is saved). */
export type AcceptMode = 'image' | 'logo' | 'design3d' | 'document' | 'any';

export interface ValidationConstraints {
  accept: AcceptMode;
  /** Max size per file in bytes. Defaults based on accept mode. */
  maxSize?: number;
  /** Max total files allowed (existing + new). Omit for unlimited. */
  maxCount?: number;
  /** Number of files already present — for enforcing maxCount across the add. */
  existingCount?: number;
  /** Override the allowed extension list (e.g. allow only ['.pdf']). */
  allowedExtensions?: readonly string[];
}

export type RejectReason = 'blocked_extension' | 'wrong_type' | 'too_large' | 'max_count';

export interface RejectedFile {
  file: File;
  reason: RejectReason;
  message: string;
}

export interface ValidationResult {
  accepted: File[];
  rejected: RejectedFile[];
  allValid: boolean;
}

// ── Helpers ───────────────────────────────────────────────────────

function extOf(name: string): string {
  const idx = name.lastIndexOf('.');
  return idx >= 0 ? name.slice(idx).toLowerCase() : '';
}

/** The per-file cap a drop zone applies when the caller sets none — the same
 *  number `validateFiles` checks and the upload refuses before sending. */
export function defaultMaxSize(accept: AcceptMode): number {
  switch (accept) {
    case 'image':
    case 'logo': return MAX_SIZE_IMAGE;
    case 'design3d': return MAX_SIZE_3D;
    case 'document': return MAX_SIZE_DOC;
    default: return MAX_SIZE_ANY;
  }
}

function defaultAllowedExtensions(accept: AcceptMode): readonly string[] | null {
  switch (accept) {
    case 'image': return IMAGE_EXTENSIONS;
    case 'logo': return LOGO_EXTENSIONS;
    case 'design3d': return [...DESIGN_3D_EXTENSIONS, ...ARCHIVE_EXTENSIONS];
    case 'document': return [...DOC_EXTENSIONS, ...ARCHIVE_EXTENSIONS];
    case 'any': return null; // any permitted extension (as long as not blocked)
  }
}

/** The `accept` attribute of a hidden `<input type="file">` for `accept`, so
 *  the picker offers what the drop zone takes (undefined = anything). The
 *  picked files still go through `validateFiles`: a picker can be told to show
 *  every file. `image` stays `image/*` so a phone keeps offering its camera. */
export function inputAcceptFor(accept: AcceptMode): string | undefined {
  switch (accept) {
    case 'image': return 'image/*';
    case 'logo': return LOGO_EXTENSIONS.join(',');
    case 'design3d': return [...DESIGN_3D_EXTENSIONS, ...ARCHIVE_EXTENSIONS].join(',');
    case 'document': return [...DOC_EXTENSIONS, ...ARCHIVE_EXTENSIONS].join(',');
    case 'any': return undefined;
  }
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${Math.round(bytes / (1024 * 1024))} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

// ── i18n seam ─────────────────────────────────────────────────────
// fileValidation is a framework-agnostic lib used by BOTH the tenant (i18n) and
// admin (Spanish-only), plus shared components that have no LocaleContext. So
// translations are injected via a module-level label set with Spanish defaults:
// the tenant registers t()-backed labels once (re-registering on locale change)
// via `setFileValidationLabels`; admin registers nothing and keeps Spanish. No
// call-site or component-prop changes are needed.

export interface FileValidationLabels {
  blockedExtension: (name: string, ext: string) => string;
  /** `allowed` is the list that goes there («.stl, .obj, …»): saying only
   *  «not allowed» left a workshop guessing seven times (2026-10-27). */
  wrongType: (name: string, ext: string, allowed: string) => string;
  tooLarge: (name: string, maxSize: string) => string;
  maxCount: (name: string, maxCount: number) => string;
  /** Shown in place of a missing extension. */
  noExtension: string;
  groupBlocked: (n: number) => string;
  groupWrongType: (n: number) => string;
  groupTooLarge: (n: number) => string;
  groupMaxCount: (n: number) => string;
  /** Wrapper for the multi-file summary (always plural — only used for ≥2 files). */
  summary: (total: number, parts: string) => string;
  /** Shown when a selected file can no longer be read at upload time — the
   *  user moved, edited or deleted it after picking it (Chrome surfaces this
   *  as net::ERR_UPLOAD_FILE_CHANGED). ``name`` is empty when unknown. */
  fileChanged: (name: string) => string;
  /** Under a file that is uploading: «45 % · 18 MB de 40 MB». */
  uploadProgress: (pct: number, loaded: string, total: string) => string;
  /** The connection dropped; the upload starts again by itself. */
  uploadRetrying: string;
  /** No connection; it retries when the browser is back online. */
  uploadOffline: string;
  /** The upload gave up (a refusal, or every retry failed). */
  uploadFailed: string;
  /** Button on a failed upload. */
  uploadRetry: string;
  /** Button that cancels an upload in progress / drops a failed one. */
  uploadCancel: string;
}

const DEFAULT_LABELS: FileValidationLabels = {
  blockedExtension: (name, ext) => `"${name}" tiene una extensión bloqueada (${ext})`,
  wrongType: (name, ext, allowed) => `"${name}" no es un tipo de archivo permitido aquí (${ext}). Se admiten: ${allowed}`,
  tooLarge: (name, maxSize) => `"${name}" supera el tamaño máximo de ${maxSize}`,
  maxCount: (name, maxCount) => `"${name}" no cabe — máximo ${maxCount} archivos en total`,
  noExtension: 'sin extensión',
  groupBlocked: (n) => `${n} con extensión bloqueada`,
  groupWrongType: (n) => `${n} de tipo no permitido`,
  groupTooLarge: (n) => `${n} demasiado grandes`,
  groupMaxCount: (n) => `${n} exceden el máximo`,
  summary: (total, parts) => `${total} archivos rechazados: ${parts}`,
  fileChanged: (name) =>
    name
      ? `No se pudo leer «${name}». Es posible que lo hayas movido, editado o eliminado después de seleccionarlo. Vuelve a seleccionarlo e inténtalo de nuevo.`
      : 'No se pudo leer el archivo. Es posible que lo hayas movido, editado o eliminado después de seleccionarlo. Vuelve a seleccionarlo e inténtalo de nuevo.',
  uploadProgress: (pct, loaded, total) => `${pct} % · ${loaded} de ${total}`,
  uploadRetrying: 'Se cortó la conexión · reintentando…',
  uploadOffline: 'Sin conexión · se reintentará',
  uploadFailed: 'No se pudo subir',
  uploadRetry: 'Reintentar',
  uploadCancel: 'Cancelar subida',
};

let activeLabels: FileValidationLabels = DEFAULT_LABELS;

/**
 * Register localized file-validation messages. The tenant calls this with
 * t()-backed labels (re-registering whenever the locale changes); pass `null`
 * to reset to the Spanish defaults. Admin never calls it → stays Spanish.
 */
export function setFileValidationLabels(labels: FileValidationLabels | null): void {
  activeLabels = labels ?? DEFAULT_LABELS;
}

/** The labels an upload in progress shows (`UploadIndicator`, file rows). */
export function getUploadLabels(): Pick<FileValidationLabels,
  'uploadProgress' | 'uploadRetrying' | 'uploadOffline' | 'uploadFailed' | 'uploadRetry' | 'uploadCancel'> {
  return activeLabels;
}

/** Localized "the file changed on disk after you selected it" message. Used by
 *  the upload guards in ``fileUpload.ts`` so the message follows the same
 *  tenant-localized / admin-Spanish registry as the validation labels. */
export function getFileChangedLabel(name: string): string {
  return activeLabels.fileChanged(name);
}

// ── Public API ────────────────────────────────────────────────────

/**
 * Validate a batch of files against constraints.
 *
 * Rules applied in order:
 *   1. Extension in BLOCKED_EXTENSIONS → blocked_extension
 *   2. accept !== 'any' and extension not in allowed list → wrong_type
 *   3. File size > maxSize → too_large
 *   4. existingCount + accepted.length > maxCount → max_count (surplus files)
 *
 * Returns `allValid=true` only if NO file was rejected. Callers should treat
 * `allValid=false` as a full cancellation (do not upload any of the accepted
 * files either) to keep UX predictable.
 */
export function validateFiles(
  files: File[],
  constraints: ValidationConstraints,
): ValidationResult {
  const maxSize = constraints.maxSize ?? defaultMaxSize(constraints.accept);
  const allowed = constraints.allowedExtensions ?? defaultAllowedExtensions(constraints.accept);
  const maxCount = constraints.maxCount;
  const existingCount = constraints.existingCount ?? 0;

  const accepted: File[] = [];
  const rejected: RejectedFile[] = [];

  for (const file of files) {
    const ext = extOf(file.name);

    if (BLOCKED_EXTENSIONS.includes(ext)) {
      rejected.push({
        file,
        reason: 'blocked_extension',
        message: activeLabels.blockedExtension(file.name, ext || activeLabels.noExtension),
      });
      continue;
    }

    if (allowed && !allowed.includes(ext)) {
      rejected.push({
        file,
        reason: 'wrong_type',
        message: activeLabels.wrongType(file.name, ext || activeLabels.noExtension, allowed.join(', ')),
      });
      continue;
    }

    if (file.size > maxSize) {
      rejected.push({
        file,
        reason: 'too_large',
        message: activeLabels.tooLarge(file.name, formatBytes(maxSize)),
      });
      continue;
    }

    accepted.push(file);
  }

  // Count check runs after per-file checks so messages are clearer
  if (maxCount !== undefined && existingCount + accepted.length > maxCount) {
    const overflow = existingCount + accepted.length - maxCount;
    const surplus = accepted.splice(accepted.length - overflow, overflow);
    for (const f of surplus) {
      rejected.push({
        file: f,
        reason: 'max_count',
        message: activeLabels.maxCount(f.name, maxCount),
      });
    }
  }

  return {
    accepted,
    rejected,
    allValid: rejected.length === 0,
  };
}

/**
 * Summarise a list of rejected files into a user-facing toast message.
 * Groups by reason so the toast is concise.
 */
export function summarizeRejections(rejected: RejectedFile[]): string {
  if (rejected.length === 0) return '';
  if (rejected.length === 1) return rejected[0]!.message;

  const byReason: Record<RejectReason, RejectedFile[]> = {
    blocked_extension: [],
    wrong_type: [],
    too_large: [],
    max_count: [],
  };
  for (const r of rejected) byReason[r.reason].push(r);

  const parts: string[] = [];
  if (byReason.blocked_extension.length) {
    parts.push(activeLabels.groupBlocked(byReason.blocked_extension.length));
  }
  if (byReason.wrong_type.length) {
    parts.push(activeLabels.groupWrongType(byReason.wrong_type.length));
  }
  if (byReason.too_large.length) {
    parts.push(activeLabels.groupTooLarge(byReason.too_large.length));
  }
  if (byReason.max_count.length) {
    parts.push(activeLabels.groupMaxCount(byReason.max_count.length));
  }

  return activeLabels.summary(rejected.length, parts.join(', '));
}

/**
 * Derive the visual "kind" of a file from its extension, for icon rendering.
 */
export type FileKind = 'image' | 'pdf' | 'stl' | 'archive' | 'doc' | 'other';

export function deriveFileKind(name: string): FileKind {
  const ext = extOf(name);
  if (IMAGE_EXTENSIONS.includes(ext)) return 'image';
  if (ext === '.pdf') return 'pdf';
  if (DESIGN_3D_EXTENSIONS.includes(ext)) return 'stl';
  if (ARCHIVE_EXTENSIONS.includes(ext)) return 'archive';
  if (DOC_EXTENSIONS.includes(ext)) return 'doc';
  return 'other';
}
